import type {
  MisoCaseEvent,
  MisoContingencyReserveDeployment,
  MisoDispatchBlock,
  MisoDispatchInstruction,
  MisoEmergencyPayload,
  MisoNotificationItem,
  MisoReserveZoneStudyStatus,
  MisoResourceStartStopInstruction,
  MisoUnconfirmedContract,
  MisoNotificationsByHour,
  MisoNotificationsForSiteResponse,
  MisoNotificationSummary,
  MisoNotificationType,
  MisoRegion,
} from '@/types/eos/MisoNotifications'
import { formatCurrency, formatMegawatts } from '@/util/formatters'
import { isPlainObject, startCase, uniq, uniqBy } from 'lodash'
import { DateTime } from 'luxon'
import { formatTimeWindow } from './table-formatters'

/**
 * MISO timestamps its notifications in EST year-round (a fixed -05:00 offset, no DST),
 * so the endpoint's `day` param is a calendar day in that fixed zone -- not America/New_York.
 */
export const MISO_ZONE = 'UTC-5'

export const MISO_NOTIFICATION_TYPE_LABELS: Record<MisoNotificationType, string> = {
  'emergency-notification': 'MISO Emergency Notification',
  'dispatch-instruction': 'MISO Dispatch Instruction',
  'contingency-reserve': 'MISO Contingency Reserve',
  'case-event': 'MISO Case Event',
  'resource-start-stop': 'MISO Resource Start/Stop',
  'reserve-zone-study-status': 'MISO Reserve Zone Study Status',
  'financial-contract-schedule-confirmation': 'MISO Financial Contract Schedule Confirmation',
  unknown: 'MISO Notification',
}

const MISO_REGIONS: MisoRegion[] = ['NORTH', 'CENTRAL', 'SOUTH']

/** Matches MISO's free-text "Affected Region(s): NORTH, CENTRAL" line. */
const AFFECTED_REGIONS_LINE = /affected\s+region\(?s?\)?\s*:\s*(.*)/i

/**
 * Lists the MISO (EST) days a dashboard window covers.
 *
 * The dashboard window starts at midnight in the user's selected timezone, so it
 * usually straddles two EST days -- and the endpoint serves one EST day per call.
 *
 * @param {DateTime | undefined} startHour the first hour of the window (hour-beginning).
 * @param {number} hours the window length in hours.
 * @returns {string[]} 'YYYY-MM-DD' EST days, ascending. Empty for an invalid start hour.
 */
export const misoDaysForWindow = (startHour: DateTime | undefined, hours: number = 24): string[] => {
  if (!startHour?.isValid) return []

  const first = startHour.setZone(MISO_ZONE).startOf('day')
  const last = startHour
    .setZone(MISO_ZONE)
    .plus({ hours: hours - 1 })
    .startOf('day')

  const days: string[] = []
  for (let day = first; day <= last; day = day.plus({ days: 1 })) {
    days.push(day.toFormat('yyyy-MM-dd'))
  }
  return days
}

/**
 * Key used to join a timestamp to the notifications of the hour it falls in.
 * Truncates to the hour and normalizes through Luxon, so the dashboard's hours,
 * MISO's hours, and arbitrary received-at timestamps all land on the same key.
 *
 * @param {string} isoDateTime an ISO 8601 datetime string.
 * @returns {number} the containing hour-beginning as UTC epoch milliseconds.
 */
export const misoHourKey = (isoDateTime: string): number =>
  DateTime.fromISO(isoDateTime, { zone: 'utc' }).startOf('hour').toMillis()

/**
 * Extracts the regions a notification's text names.
 *
 * Prefers MISO's "Affected Region(s): ..." line and falls back to the whole text,
 * since some alerts only name the region in prose ("...for the MISO South Region.").
 *
 * @param {string} text a message's text.
 * @returns {MisoRegion[]} the regions named, or an empty array if none were.
 */
export const parseAffectedRegions = (text: string): MisoRegion[] => {
  const scope = AFFECTED_REGIONS_LINE.exec(text)?.[1] ?? text
  const upperCasedScope = scope.toUpperCase()
  return MISO_REGIONS.filter((region) => upperCasedScope.includes(region))
}

/**
 * Pulls the human-readable message texts out of a notification's payload.
 * Only emergency notifications carry them; every other type returns nothing.
 *
 * @param {MisoNotificationItem['payload']} payload the raw MISO body.
 * @returns {string[]} de-duped, non-empty message texts.
 */
const getPayloadMessages = (payload: MisoNotificationItem['payload']): string[] => {
  const messages = (payload?.notificationData as MisoEmergencyPayload['notificationData'] | undefined)?.messages
  if (!Array.isArray(messages)) return []

  return uniq(messages.map((message) => message?.text?.trim()).filter((text): text is string => !!text))
}

/**
 * Flattens a stored notification of any type for display.
 *
 * @param {MisoNotificationItem} item the notification.
 * @returns {MisoNotificationSummary} the flattened notification.
 */
export const toMisoNotificationSummary = (item: MisoNotificationItem): MisoNotificationSummary => {
  const messages = getPayloadMessages(item.payload)

  return {
    id: item.id,
    type: item.notification_type ?? 'unknown',
    isTest: item.is_test,
    receivedAt: item.received_at,
    startUtc: item.start_utc,
    endUtc: item.end_utc,
    messages,
    regions: uniq(messages.flatMap(parseAffectedRegions)),
    payload: item.payload,
  }
}

/**
 * Whether a notification is an emergency alert -- the ones worth calling out in the table.
 *
 * Detected from the payload carrying MISO's `messages`, since `notification_type` is the
 * ingesting service's best-effort classification rather than something MISO guarantees.
 * A time window no longer distinguishes these: dispatch instructions are windowed too.
 *
 * @param {MisoNotificationSummary} notification the notification to check.
 * @returns {boolean} true if it's an emergency alert.
 */
export const isMisoEmergencyAlert = (notification: MisoNotificationSummary): boolean =>
  !!notification.messages.length || notification.type === 'emergency-notification'

/** Emergency alerts first, then oldest-received first. */
const byEmergencyThenReceivedAt = (a: MisoNotificationSummary, b: MisoNotificationSummary): number => {
  const [aIsEmergency, bIsEmergency] = [isMisoEmergencyAlert(a), isMisoEmergencyAlert(b)]
  if (aIsEmergency !== bIsEmergency) return aIsEmergency ? -1 : 1
  return a.receivedAt.localeCompare(b.receivedAt)
}

/**
 * Transforms one or more days of MISO notifications into the notifications to show per hour.
 *
 * Windowed notifications (emergency alerts) are placed on every hour the server reports them
 * active in. Everything else has no server-derived window, so it's placed on the hour MISO
 * sent it -- keyed off `start_utc` being null, not off absence from `hours[]`, since a
 * non-windowed notification's `received_at` can fall outside the requested day.
 *
 * @param {MisoNotificationsForSiteResponse[]} responses one response per requested EST day.
 * @returns {MisoNotificationsByHour} notifications keyed by hour-beginning UTC epoch millis.
 * Hours without notifications are omitted.
 */
export const buildMisoNotificationsByHour = (
  responses: MisoNotificationsForSiteResponse[]
): MisoNotificationsByHour => {
  const notificationsById = new Map<number, MisoNotificationSummary>()
  for (const { notifications } of responses) {
    for (const item of notifications) {
      const notification = toMisoNotificationSummary(item)
      notificationsById.set(notification.id, notification)
    }
  }

  const notificationsByHour: MisoNotificationsByHour = new Map()
  const addToHour = (isoDateTime: string, notifications: MisoNotificationSummary[]) => {
    if (!notifications.length) return
    const key = misoHourKey(isoDateTime)
    notificationsByHour.set(key, uniqBy([...(notificationsByHour.get(key) ?? []), ...notifications], 'id'))
  }

  // Windowed notifications: the server tells us which hours they're active in.
  for (const { hours } of responses) {
    for (const { hour_utc, active_notification_ids } of hours) {
      const active = active_notification_ids
        .map((id) => notificationsById.get(id))
        .filter((notification): notification is MisoNotificationSummary => !!notification)

      addToHour(hour_utc, active)
    }
  }

  // Everything else: no window to band, so it lands on the hour it was received.
  for (const notification of notificationsById.values()) {
    if (notification.startUtc) continue
    addToHour(notification.receivedAt, [notification])
  }

  for (const notifications of notificationsByHour.values()) {
    notifications.sort(byEmergencyThenReceivedAt)
  }

  return notificationsByHour
}

/** Payload lines rendered per notification before we stop and count the rest. */
const MAX_PAYLOAD_LINES = 5

/**
 * Formats a timestamp MISO put inside a payload. Those are outside our control, so an
 * unparseable one is passed through rather than rendered as "Invalid DateTime".
 *
 * @param {string} isoDateTime the timestamp to format.
 * @param {string} tz the timezone to display it in.
 * @returns {string} the formatted timestamp.
 */
const formatMisoDateTime = (isoDateTime: string, tz?: string): string => {
  const dateTime = DateTime.fromISO(isoDateTime, { zone: tz })
  return dateTime.isValid ? dateTime.toFormat('MMM dd HH:mm') : isoDateTime
}

/** The payload's `notificationData` object, or null if MISO didn't send one. */
const getNotificationData = (payload: MisoNotificationItem['payload']): Record<string, unknown> | null =>
  isPlainObject(payload?.notificationData) ? (payload.notificationData as Record<string, unknown>) : null

/** A payload value we can render as-is, or null if it isn't a scalar. */
const formatScalar = (value: unknown): string | null => {
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (typeof value === 'string') return value.trim() || null
  return null
}

/**
 * Formats a market operating day ('YYYY-MM-DD'). Those name a calendar day rather than an
 * instant, so they're read in UTC -- displaying them in a negative-offset zone would shift
 * them back a day.
 *
 * @param {string} day the market day.
 * @returns {string} the formatted day.
 */
const formatMisoDay = (day: string): string => {
  const parsed = DateTime.fromISO(day, { zone: 'utc' })
  return parsed.isValid ? parsed.toFormat('MMM dd') : day
}

/** Reads a `notificationData` collection as entries of a known shape, ignoring anything else. */
const getEntries = <T>(value: unknown): T[] => (Array.isArray(value) ? (value.filter(isPlainObject) as T[]) : [])

/** Joins the parts of a line, dropping the ones MISO left out. */
const joinParts = (parts: Array<string | null | undefined>, separator: string): string =>
  parts.filter(Boolean).join(separator)

/** "<time> - <subject>", either half optional. */
const describeSubjectAt = (isoDateTime: string | undefined, subject: string | undefined, tz?: string): string =>
  joinParts([isoDateTime ? formatMisoDateTime(isoDateTime, tz) : null, subject], ' - ')

/**
 * Renders an object's scalar fields as "Label: value", descending into nested objects
 * since MISO groups values that way (`energyDispatch.mw`).
 *
 * @param {Record<string, unknown>} fields the object to describe.
 * @returns {string[]} one entry per scalar field found.
 */
const describeFields = (fields: Record<string, unknown>): string[] =>
  Object.entries(fields).flatMap(([key, value]) => {
    if (isPlainObject(value)) return describeFields(value as Record<string, unknown>)
    const scalar = formatScalar(value)
    return scalar ? [`${startCase(key)}: ${scalar}`] : []
  })

/** Describes a payload's `notificationData` collections, given the notification's type. */
type PayloadDescriber = (notificationData: Record<string, unknown>, tz?: string) => string[]

/** The dispatch blocks an instruction can carry, in the order MISO documents them. */
const DISPATCH_BLOCKS: Array<{ key: keyof MisoDispatchInstruction; label: string }> = [
  { key: 'energyDispatch', label: 'Energy' },
  { key: 'regulationDispatch', label: 'Regulation' },
  { key: 'spinningReserveDispatch', label: 'Spinning reserve' },
  { key: 'supplementalReserveDispatch', label: 'Supplemental reserve' },
  { key: 'shortTermReserveDispatch', label: 'Short-term reserve' },
  { key: 'rampCapabilityUpDispatch', label: 'Ramp up' },
  { key: 'rampCapabilityDownDispatch', label: 'Ramp down' },
]

/**
 * Describes one dispatched product the way the EOS table shows load and price. `lmp` carries
 * the price on the energy block, `mcp` on the others.
 *
 * @param {string} label the product's name.
 * @param {MisoDispatchBlock | null | undefined} block the dispatched values.
 * @returns {string | null} e.g. "Energy 120.500 MW @ $34.91/MWh", or null if MISO dispatched nothing.
 */
const describeDispatchBlock = (label: string, block: MisoDispatchBlock | null | undefined): string | null => {
  if (block?.mw == null) return null

  const price = block.lmp ?? block.mcp
  return joinParts(
    [
      `${label} ${formatMegawatts(block.mw)} MW`,
      price == null ? null : `@ ${formatCurrency(price)}/MWh`,
      block.udeFlag ? '(unit dispatch error)' : null,
    ],
    ' '
  )
}

/** Dispatch instructions: when, to which pnode, and what was dispatched. */
const describeDispatchInstructions: PayloadDescriber = (notificationData, tz) =>
  getEntries<MisoDispatchInstruction>(notificationData.dispatchInstructions).map((instruction) => {
    // Every block is required by the spec, so a resource providing no reserves reports zeroes.
    // Only energy is worth showing unconditionally.
    const blocks = DISPATCH_BLOCKS.filter(
      ({ key }) => key === 'energyDispatch' || (instruction[key] as MisoDispatchBlock | null)?.mw
    ).map(({ key, label }) => describeDispatchBlock(label, instruction[key] as MisoDispatchBlock | null))

    return joinParts(
      [describeSubjectAt(instruction.interval, instruction.pnodeName, tz), joinParts(blocks, ' · ')],
      ': '
    )
  })

/** Contingency reserve deployments: when, to which pnode, and how much. */
const describeContingencyReserveDeployments: PayloadDescriber = (notificationData, tz) =>
  getEntries<MisoContingencyReserveDeployment>(notificationData.crDeployments).map((deployment) =>
    joinParts(
      [
        describeSubjectAt(deployment.timestamp, deployment.pnodeName, tz),
        deployment.mw == null ? null : `${formatMegawatts(deployment.mw)} MW deployed`,
      ],
      ': '
    )
  )

/** Case events: which case cleared, for which interval and market. */
const describeCaseEvents: PayloadDescriber = (notificationData, tz) =>
  getEntries<MisoCaseEvent>(notificationData.caseEvents).map((event) => {
    const context = joinParts([event.studyModeShortName, event.market, event.priceType], ', ')

    return joinParts(
      [
        describeSubjectAt(event.interval, event.eventType ? startCase(event.eventType) : undefined, tz),
        context && `(${context})`,
      ],
      ' '
    )
  })

/** Resource start/stop instructions, labelled by which list they came from. */
const describeResourceInstructions = (value: unknown, action: string, tz?: string): string[] =>
  getEntries<MisoResourceStartStopInstruction>(value).map((instruction) => {
    const reason = joinParts([instruction.deployStatus, instruction.deployReason], ', ')

    return joinParts(
      [
        joinParts([describeSubjectAt(instruction.interval, instruction.pnodeName, tz), action], ': '),
        reason && `(${reason})`,
      ],
      ' '
    )
  })

const describeResourceStartStops: PayloadDescriber = (notificationData, tz) => [
  ...describeResourceInstructions(notificationData.resourceStartInstructions, 'Start', tz),
  ...describeResourceInstructions(notificationData.resourceStopInstructions, 'Stop', tz),
]

/** Reserve zone study status, per market day. */
const describeReserveZoneStudyStatus: PayloadDescriber = (notificationData) =>
  getEntries<MisoReserveZoneStudyStatus>(notificationData.reserveZoneStudyStatus)
    .map((status) => joinParts([status.day ? formatMisoDay(status.day) : null, status.status], ' - '))
    .filter(Boolean)

/** Unconfirmed financial contracts and schedules, with their counterparties. */
const describeUnconfirmedContracts = (value: unknown, label: string): string[] =>
  getEntries<MisoUnconfirmedContract>(value).map((contract) => {
    const details = joinParts(
      [
        contract.contractType,
        contract.scheduleDay ? formatMisoDay(contract.scheduleDay) : null,
        contract.buyerName && `buyer ${contract.buyerName}`,
        contract.sellerName && `seller ${contract.sellerName}`,
      ],
      ' · '
    )

    return joinParts([`${label}: ${contract.contractName ?? 'unnamed'}`, details], ' - ')
  })

const describeFinancialContractSchedules: PayloadDescriber = (notificationData) => [
  ...describeUnconfirmedContracts(notificationData.unconfirmedContracts, 'Unconfirmed contract'),
  ...describeUnconfirmedContracts(notificationData.unconfirmedSchedules, 'Unconfirmed schedule'),
]

/**
 * Every payload describer. They're run against the payload rather than selected by
 * `notification_type`, since that field is the ingesting service's best-effort
 * classification -- each describer simply yields nothing when its collection is absent.
 * Emergency notifications are absent from the list: their `messages` are MISO's own prose,
 * which we render verbatim ahead of everything else.
 */
const PAYLOAD_DESCRIBERS: PayloadDescriber[] = [
  describeDispatchInstructions,
  describeContingencyReserveDeployments,
  describeCaseEvents,
  describeResourceStartStops,
  describeReserveZoneStudyStatus,
  describeFinancialContractSchedules,
]

/**
 * Best-effort description of a payload we have no describer for: every collection in
 * `notificationData`, one line per entry. Keeps a type MISO adds later legible.
 *
 * @param {Record<string, unknown>} notificationData the payload's notificationData.
 * @returns {string[]} one line per entry found.
 */
const describeUnmodeledPayload = (notificationData: Record<string, unknown>): string[] =>
  Object.entries(notificationData).flatMap(([key, value]) => {
    const entries = Array.isArray(value) ? value : [value]

    return entries.flatMap((entry) => {
      const details = isPlainObject(entry) ? describeFields(entry as Record<string, unknown>).join(' · ') : null
      const line = details || formatScalar(entry)
      return line ? [`${startCase(key)}: ${line}`] : []
    })
  })

/**
 * Describes a notification's payload for display, in the friendliest form available: MISO's
 * own prose first, then purpose-built lines for every modelled collection the payload carries,
 * falling back to a generic field walk when none of them match.
 *
 * @param {MisoNotificationSummary} notification the notification to describe.
 * @param {string} tz the timezone to display times in.
 * @returns {string[]} the lines to render, capped at MAX_PAYLOAD_LINES plus a count of the rest.
 */
export const describeMisoPayload = (notification: MisoNotificationSummary, tz?: string): string[] => {
  const notificationData = getNotificationData(notification.payload)
  if (!notificationData) return notification.messages

  // MISO's own wording comes first -- nothing we build beats it.
  const described = [
    ...notification.messages,
    ...PAYLOAD_DESCRIBERS.flatMap((describe) => describe(notificationData, tz)),
  ].filter(Boolean)
  const lines = described.length ? described : describeUnmodeledPayload(notificationData)

  if (lines.length <= MAX_PAYLOAD_LINES) return lines
  return [...lines.slice(0, MAX_PAYLOAD_LINES), `...and ${lines.length - MAX_PAYLOAD_LINES} more`]
}

/**
 * Formats a notification's active window for display.
 *
 * MISO doesn't always send a termination time; such an alert stays active indefinitely,
 * so we label it as open-ended rather than showing a missing end.
 *
 * @param {MisoNotificationSummary} notification the notification to format.
 * @param {string} tz the timezone to display it in.
 * @returns {string | null} the formatted window, or null if the notification has none.
 */
export const formatMisoNotificationWindow = (notification: MisoNotificationSummary, tz?: string): string | null => {
  if (!notification.startUtc) return null
  if (!notification.endUtc) return `${formatMisoDateTime(notification.startUtc, tz)} onward (no end time sent)`
  return formatTimeWindow(notification.startUtc, notification.endUtc, tz)
}

/**
 * Formats the time MISO sent a notification.
 *
 * @param {MisoNotificationSummary} notification the notification to format.
 * @param {string} tz the timezone to display it in.
 * @returns {string} the formatted received-at time.
 */
export const formatMisoReceivedAt = (notification: MisoNotificationSummary, tz?: string): string =>
  formatMisoDateTime(notification.receivedAt, tz)
